import { useState, useCallback } from 'react';
import { admin } from '../../api/client.js';
import { useApi } from '../../hooks/useApi.js';
import { Badge } from '../../components/Badge.js';
import { Modal } from '../../components/Modal.js';
import { LoadingSpinner } from '../../components/LoadingSpinner.js';
import { ErrorMessage } from '../../components/ErrorMessage.js';
import type { AdminPayment } from '../../types/index.js';

const STATE_FILTERS = [
  { value: '', label: 'All' },
  { value: 'SWEEPING', label: 'Sweeping' },
  { value: 'UNDERPAID', label: 'Underpaid' },
  { value: 'REFUNDING', label: 'Refunding' },
  { value: 'CONFIRMING', label: 'Confirming' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'EXPIRED', label: 'Expired' },
];

const PAGE_SIZE = 50;

export function AdminPaymentsPage() {
  const [stateFilter, setStateFilter] = useState('');
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<AdminPayment | null>(null);
  const [actionLoading, setActionLoading] = useState('');
  const [actionResult, setActionResult] = useState('');

  const fetcher = useCallback(
    () => admin.payments({ state: stateFilter || undefined, limit: PAGE_SIZE, offset }),
    [stateFilter, offset],
  );
  const { data, loading, error, refetch } = useApi(fetcher, [stateFilter, offset]);

  const payments = data?.payments ?? [];
  const stateCounts = data?.stateCounts ?? {};

  const stuckTotal = (stateCounts['SWEEPING'] ?? 0) + (stateCounts['CONFIRMING'] ?? 0)
    + (stateCounts['UNDERPAID'] ?? 0) + (stateCounts['REFUNDING'] ?? 0);

  const handleForceTransition = async (paymentId: string, newState: string, reason: string) => {
    setActionLoading('transition');
    setActionResult('');
    try {
      const res = await admin.forceTransition(paymentId, { newState, reason });
      setActionResult(`Transitioned: ${res.oldState} → ${res.newState}`);
      void refetch();
    } catch (err) {
      setActionResult(`Failed: ${err instanceof Error ? err.message : 'unknown error'}`);
    } finally {
      setActionLoading('');
    }
  };

  const handleRetrySweep = async (paymentId: string) => {
    setActionLoading('sweep');
    setActionResult('');
    try {
      const res = await admin.retrySweep(paymentId);
      setActionResult(res.txHash ? `Swept ${res.amount?.toFixed(6)} SOL — tx: ${res.txHash.slice(0, 12)}...` : (res.message ?? 'Done'));
      void refetch();
    } catch (err) {
      setActionResult(`Sweep failed: ${err instanceof Error ? err.message : 'unknown error'}`);
    } finally {
      setActionLoading('');
    }
  };

  if (error) return <ErrorMessage message={error} onRetry={() => void refetch()} />;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontFamily: 'var(--fd)', fontSize: 18, fontWeight: 700, letterSpacing: '-0.02em', marginBottom: 4 }}>All Payments</h2>
          <p style={{ fontSize: 13, color: 'var(--t3)' }}>Payment sessions across every server on the platform</p>
        </div>
      </div>

      {/* summary pills */}
      <div className="stats-row">
        <div className="stat-card">
          <div className="stat-label">Stuck / Needs Attention</div>
          <div className="stat-value" style={{ color: stuckTotal > 0 ? 'var(--red)' : 'var(--green)' }}>{stuckTotal}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Failed</div>
          <div className="stat-value" style={{ color: (stateCounts['FAILED'] ?? 0) > 0 ? 'var(--red)' : 'var(--t3)' }}>{stateCounts['FAILED'] ?? 0}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Completed</div>
          <div className="stat-value" style={{ color: 'var(--green)' }}>{stateCounts['COMPLETED'] ?? 0}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Total</div>
          <div className="stat-value">{Object.values(stateCounts).reduce((a, b) => a + b, 0)}</div>
        </div>
      </div>

      {/* filters */}
      <div className="filter-row">
        {STATE_FILTERS.map((f) => (
          <div
            key={f.value}
            className={`filter-pill ${stateFilter === f.value ? 'active' : ''}`}
            onClick={() => { setStateFilter(f.value); setOffset(0); }}
          >
            {f.label}
            {f.value && stateCounts[f.value] ? ` (${stateCounts[f.value]})` : ''}
          </div>
        ))}
      </div>

      {loading && <LoadingSpinner text="Loading payments..." />}

      {!loading && payments.length > 0 && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Session</th>
                <th>Server</th>
                <th>User</th>
                <th>Status</th>
                <th>Expected</th>
                <th>Received</th>
                <th>Age</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((p) => (
                <tr key={p.id} style={{ cursor: 'pointer' }} onClick={() => setSelected(p)}>
                  <td style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--t3)' }}>
                    {p.id.slice(0, 4)}...{p.id.slice(-4)}
                  </td>
                  <td style={{ color: 'var(--t2)', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {p.serverName}
                  </td>
                  <td style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--t3)' }}>
                    {p.discordUserId.slice(0, 8)}...
                  </td>
                  <td><Badge status={p.state} label={p.state.replace(/_/g, ' ')} /></td>
                  <td style={{ fontFamily: 'var(--fd)', fontWeight: 600 }}>{Number(p.expectedAmount).toFixed(4)}</td>
                  <td style={{
                    fontFamily: 'var(--fd)', fontWeight: 600,
                    color: Number(p.receivedAmount) >= Number(p.expectedAmount) ? 'var(--green)' : Number(p.receivedAmount) > 0 ? 'var(--red)' : 'var(--t3)',
                  }}>{Number(p.receivedAmount).toFixed(4)}</td>
                  <td style={{ color: 'var(--t3)', fontSize: 12 }}>{timeAgo(p.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* pagination */}
      {payments.length === PAGE_SIZE && (
        <div className="pagination">
          <button className="btn-sm btn-ghost" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>Previous</button>
          <button className="btn-sm btn-ghost" onClick={() => setOffset(offset + PAGE_SIZE)}>Next</button>
        </div>
      )}

      {/* detail modal */}
      {selected && (
        <Modal open title="Payment Detail" onClose={() => { setSelected(null); setActionResult(''); }} width={600}>
          <div style={{ padding: '0 0 20px', display: 'grid', gap: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <Detail label="Session ID" value={selected.id} mono />
              <Detail label="Server" value={selected.serverName} />
              <Detail label="Discord User" value={selected.discordUserId} mono />
              <Detail label="Plan" value={selected.planName ?? '—'} />
              <Detail label="State" value={selected.state} />
              <Detail label="Expected" value={`${Number(selected.expectedAmount).toFixed(6)} SOL`} />
              <Detail label="Received" value={`${Number(selected.receivedAmount).toFixed(6)} SOL`} />
              <Detail label="USD" value={`$${Number(selected.priceUsd).toFixed(2)}`} />
              <Detail label="Deposit" value={selected.depositAddress.slice(0, 12) + '...'} mono />
              <Detail label="Incoming TX" value={selected.incomingTx ? selected.incomingTx.slice(0, 12) + '...' : '—'} mono />
              <Detail label="Sweep TX" value={selected.sweepTx ? selected.sweepTx.slice(0, 12) + '...' : '—'} mono />
              <Detail label="Created" value={new Date(selected.createdAt).toLocaleString()} />
              <Detail label="Updated" value={new Date(selected.updatedAt).toLocaleString()} />
            </div>

            {actionResult && (
              <div style={{ fontSize: 13, padding: '8px 12px', background: 'var(--s2)', borderRadius: 8, border: '1px solid var(--b1)' }}>
                {actionResult}
              </div>
            )}

            {/* actions */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
              {['SWEEPING', 'CONFIRMING'].includes(selected.state) && !selected.sweepTx && (
                <button
                  className="btn-sm btn-primary"
                  disabled={actionLoading === 'sweep'}
                  onClick={() => void handleRetrySweep(selected.id)}
                >
                  {actionLoading === 'sweep' ? 'Sweeping...' : 'Retry Sweep'}
                </button>
              )}

              {['SWEEPING', 'CONFIRMING', 'UNDERPAID', 'REFUNDING'].includes(selected.state) && (
                <button
                  className="btn-sm btn-ghost"
                  disabled={!!actionLoading}
                  onClick={() => void handleForceTransition(selected.id, 'COMPLETED', 'Admin manually completed')}
                >
                  Force Complete
                </button>
              )}

              {['SWEEPING', 'CONFIRMING', 'UNDERPAID', 'REFUNDING', 'AWAITING_PAYMENT'].includes(selected.state) && (
                <button
                  className="btn-sm btn-ghost"
                  style={{ color: 'var(--red)' }}
                  disabled={!!actionLoading}
                  onClick={() => void handleForceTransition(selected.id, 'EXPIRED', 'Admin manually expired')}
                >
                  Force Expire
                </button>
              )}

              {selected.state === 'FAILED' && (
                <>
                  <button
                    className="btn-sm btn-ghost"
                    disabled={!!actionLoading}
                    onClick={() => void handleForceTransition(selected.id, 'SWEEPING', 'Admin retry from failed')}
                  >
                    Retry (→ Sweeping)
                  </button>
                  <button
                    className="btn-sm btn-ghost"
                    style={{ color: 'var(--red)' }}
                    disabled={!!actionLoading}
                    onClick={() => void handleForceTransition(selected.id, 'EXPIRED', 'Admin expired failed session')}
                  >
                    Expire
                  </button>
                </>
              )}
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

function Detail({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--t3)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13, color: 'var(--t1)', fontFamily: mono ? 'monospace' : undefined, wordBreak: 'break-all' }}>{value}</div>
    </div>
  );
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}
